import { useEffect, useRef, useState } from 'react';
import { openRemoteFile } from '@/utils/saveFile';
import {
  Avatar,
  Box,
  Flex,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import seenGreen from '@/svg/seen-green.svg';
import seenWhite from '@/svg/seen-white.svg';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import type { ChatMessage, MessageRead } from '../types';
import { useLongPress } from '@/hooks/useLongPress';
import { ImageBubble } from './ImageBubble';
import { MessageActionMenu } from './MessageActionMenu';
import { MessageContent } from './MessageContent';
import { PdfBubble } from './PdfBubble';

type Props = {
  message: ChatMessage;
  isOwn: boolean;
  isSeen: boolean;
  timestamp: string;
  isGroupFirst: boolean;
  isGroupLast: boolean;
  signedImageUrl?: string;
  pdfUrl?: string;
  readers: MessageRead[];
  formatFileSize: (bytes: number | null | undefined) => string;
  onOpenLightbox?: (url: string) => void;
  onScrollToBottomInstant?: () => void;
  onOpenProfile?: (userId: string) => void;
  /** Long-press actions. `canPin` is false for everyone but the event's org admins. */
  canPin?: boolean;
  isPinned?: boolean;
  onPin?: (message: ChatMessage) => void;
  onUnpin?: (messageId: string) => void;
  /** Briefly flashed after being jumped to from the pinned-message list. */
  isHighlighted?: boolean;
};

export function MessageBubble({
  message,
  isOwn,
  isSeen,
  timestamp,
  isGroupFirst,
  isGroupLast,
  signedImageUrl,
  pdfUrl,
  readers,
  formatFileSize,
  onOpenLightbox,
  onScrollToBottomInstant,
  onOpenProfile,
  canPin = false,
  isPinned = false,
  onPin,
  onUnpin,
  isHighlighted = false,
}: Props) {
  const [seenByOpen, setSeenByOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const seenByRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!seenByOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!seenByRef.current?.contains(event.target as Node)) {
        setSeenByOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [seenByOpen]);

  const hasText = message.content.trim().length > 0;

  const { isPressing, consumeClick, handlers: longPressHandlers } = useLongPress({
    onLongPress: () => {
      setSeenByOpen(false);
      setActionsOpen(true);
    },
    // Nothing to show a volunteer on a message with no text: no pin, nothing to copy.
    enabled: canPin || hasText,
  });

  const copyText = hasText
    ? () => {
        void navigator.clipboard?.writeText(message.content).catch(() => {});
      }
    : undefined;

  const avatarUrl = message.sender_avatar_url?.trim() || DEFAULT_PROFILE_IMAGE;
  const hasImage = !!message.image_url;
  const isPdf = message.type === 'pdf';
  const imageOnly = hasImage && !hasText;

  const bubble = (
    <Box
      px={imageOnly ? 0 : 2.5}
      py={imageOnly ? 0 : 1.5}
      fontSize="medium"
      lineHeight="1.2"
      borderRadius="12px"
      bg={imageOnly ? 'transparent' : isOwn ? 'blue.500' : 'gray.100'}
      color={isOwn ? 'white' : 'gray.800'}
      wordBreak="normal"
      cursor={isOwn ? 'pointer' : undefined}
      overflow="hidden"
      {...longPressHandlers}
      // A slow squeeze over roughly the hold duration. It is feedback and progress bar in
      // one: 450ms of nothing reads as lag, whereas the message visibly compressing reads
      // as the gesture being recognised. A scroll cancels the press within ~50ms, by which
      // point the transition has barely started, so flicking through the thread does not
      // strobe. useLongPress clears isPressing as it fires, so the bubble springs back at
      // the moment the menu pops out of it.
      //
      // undefined rather than scale(1) when idle: any transform other than `none` creates
      // a stacking context, and a long thread has hundreds of these. Interpolating from
      // `none` to a scale still animates -- the browser treats it as the identity matrix.
      className="press-squeeze"
      transform={isPressing ? 'scale(0.97)' : undefined}
      transition="transform 0.4s cubic-bezier(0.25, 0.8, 0.35, 1)"
      onClickCapture={(e) => {
        if (!consumeClick()) return;
        e.preventDefault();
        e.stopPropagation();
      }}
      // Long-press is ours now, so the platform's must be switched off: on iOS holding
      // text raises the selection magnifier and the native Copy/Look Up/Share callout
      // (and the save sheet on an image), and on Android it starts text selection. Both
      // fire regardless of what JavaScript does. touch-action is deliberately NOT set --
      // the thread scroller depends on it. The Copy row in MessageActionMenu is what
      // replaces the copy this takes away.
      css={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
    >
      {hasImage && (
        <ImageBubble
          signedUrl={signedImageUrl}
          isOwn={isOwn}
          mb={hasText ? 2 : 0}
          onOpenLightbox={() => {
            if (signedImageUrl) onOpenLightbox?.(signedImageUrl);
          }}
          onImageLoad={onScrollToBottomInstant}
        />
      )}

      {isPdf && message.file_url && (
        <PdfBubble
          isOwn={isOwn}
          fileName={message.file_name}
          fileSize={message.file_size}
          formattedSize={message.file_size != null ? formatFileSize(message.file_size) : undefined}
          pdfUrl={pdfUrl}
          onOpen={(url) => { void openRemoteFile(url, { title: message.file_name }); }}
        />
      )}

      {hasText ? (
        <Flex align="flex-end" gap={2} mt={imageOnly ? 0 : 0.5} px={0} pb={0}>
          {/* flex="0 1 auto", NOT flex="1". flex:1 implies flex-basis:0, so the text
              contributes nothing to the bubble's intrinsic width — the bubble then sizes
              to the nowrap timestamp beside it and short messages wrap mid-sentence.
              basis:auto lets the text size to its content and still shrink (minW:0) once
              the bubble reaches its 95% max width, so long messages wrap as before. */}
          <Text
            as="p"
            wordBreak="normal"
            overflowWrap="break-word"
            fontSize="sm"
            flex="0 1 auto"
            minW={0}
          >
            <MessageContent
              content={message.content}
              mentions={message.mentions}
              isOwn={isOwn}
              onOpenProfile={onOpenProfile}
            />
          </Text>
          <Flex as="span" align="center" justify="flex-end" gap={1} opacity={0.7} whiteSpace="nowrap">
            <Text as="span" fontSize="10px" color={isOwn ? 'blue.100' : 'gray.500'} whiteSpace="nowrap">
              {timestamp}
            </Text>
            {isOwn && (
              <Image
                as="img"
                src={isSeen ? seenGreen : seenWhite}
                alt={isSeen ? 'Seen' : 'Sent'}
                title={isSeen ? 'Seen' : 'Sent'}
                boxSize="14px"
              />
            )}
          </Flex>
        </Flex>
      ) : (
        <Flex
          as="span"
          align="center"
          justify="flex-end"
          gap={1}
          mt={imageOnly ? 0 : 0.5}
          opacity={0.7}
          px={imageOnly ? 1 : 0}
          pb={imageOnly ? 0.5 : 0}
          whiteSpace="nowrap"
        >
          <Text
            as="span"
            fontSize="10px"
            color={isOwn && imageOnly ? 'gray.500' : isOwn ? 'blue.100' : 'gray.500'}
            whiteSpace="nowrap"
          >
            {timestamp}
          </Text>
          {isOwn && (
            <Image
              as="img"
              src={isSeen ? seenGreen : seenWhite}
              alt={isSeen ? 'Seen' : 'Sent'}
              title={isSeen ? 'Seen' : 'Sent'}
              boxSize="14px"
            />
          )}
        </Flex>
      )}
    </Box>
  );

  return (
    <Flex
      // Scroll target for revealMessage(), which jumps here from the pinned-message
      // list. Same `getElementById` approach VolunteerTasksPage uses for its event rows.
      id={`chat-message-${message.id}`}
      // 85% of the message list. This is the row that alignSelf detaches from the
      // VStack's stretch, so it is the last element whose percentage still resolves
      // against the full list width — the right place for the bubble's width cap.
      maxW="85%"
      alignSelf={isOwn ? 'flex-end' : 'flex-start'}
      alignItems="flex-end"
      gap={1.5}
      flexDirection={isOwn ? 'row-reverse' : 'row'}
      // A flash rather than a persistent state: after a jump the reader needs to be told
      // which message they landed on, once. Negative margin + matching padding so the
      // highlight has some bleed around the bubble without moving anything.
      mx={-2}
      px={2}
      py={1}
      my={-1}
      borderRadius="lg"
      bg={isHighlighted ? 'yellow.100' : 'transparent'}
      transition="background-color 0.4s ease-out"
    >
      {!isOwn && (
        <Box
          flexShrink={0}
          alignSelf="flex-end"
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.200"
          overflow="hidden"
          opacity={isGroupLast ? 1 : 0}
          cursor={message.user_id ? 'pointer' : 'default'}
          onClick={() => {
            if (!message.user_id) return;
            onOpenProfile?.(message.user_id);
          }}
        >
          <Avatar.Root size="xs">
            <Avatar.Image src={avatarUrl} alt="" referrerPolicy="no-referrer" />
            <Avatar.Fallback name={message.sender_name ?? '?'} />
          </Avatar.Root>
        </Box>
      )}

      {/* maxW 100%, not 95%: a percentage here resolves against a parent that is itself
          shrink-to-fit around this content, so 95% squeezed the bubble narrower on every
          pass and short messages wrapped mid-sentence. The cap now lives on the row above. */}
      <Flex flexDirection="column" maxW="100%" minW={0} alignItems={isOwn ? 'flex-end' : 'flex-start'}>
        {message.sender_name && isGroupFirst && (
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={isOwn ? 'blue.600' : 'gray.600'}
            alignSelf={isOwn ? 'flex-end' : 'flex-start'}
            mb={0.5}
          >
            {message.sender_name}
          </Text>
        )}

        {isOwn ? (
          <Box ref={seenByRef} position="relative" display="inline-block" maxW="100%">
            <Box
              textAlign="left"
              minW={0}
              display="block"
              // The click that trails a long press never gets here: onClickCapture on the
              // bubble below stops it during capture, which is also what keeps a long
              // press on a photo from opening the lightbox underneath the menu.
              onClick={() => setSeenByOpen((open) => !open)}
            >
              {bubble}
            </Box>
            <MessageActionMenu
              open={actionsOpen}
              onClose={() => setActionsOpen(false)}
              isOwn
              onCopy={copyText}
              canPin={canPin}
              isPinned={isPinned}
              onPin={() => onPin?.(message)}
              onUnpin={() => onUnpin?.(message.id)}
            />
            {seenByOpen && (
              <Box
                position="absolute"
                right={0}
                bottom="calc(100% + 6px)"
                zIndex={10}
                minW="180px"
                maxW="240px"
                bg="white"
                borderWidth={1}
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="md"
                py={2}
                px={3}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  borderBottomWidth={1}
                  borderColor="gray.200"
                  pb={1}
                  mb={2}
                >
                  Seen by
                </Text>
                {readers.length > 0 ? (
                  <VStack align="stretch" gap={1}>
                    {readers.map((r) => (
                      <Flex key={r.user_id} justify="space-between" align="center" gap={2}>
                        <Text fontSize="sm">{r.user?.full_name ?? 'Unknown'}</Text>
                        <Text fontSize="10px" color="gray.500">
                          {new Date(r.read_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No one has read this yet.
                  </Text>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box position="relative" display="inline-block" maxW="100%">
            {bubble}
            <MessageActionMenu
              open={actionsOpen}
              onClose={() => setActionsOpen(false)}
              isOwn={false}
              onCopy={copyText}
              canPin={canPin}
              isPinned={isPinned}
              onPin={() => onPin?.(message)}
              onUnpin={() => onUnpin?.(message.id)}
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
}
